import type { CSSProperties } from 'react'
import type { GridTreeMeta } from './useTreeRows'

/**
 * Tree guide geometry.
 *
 * A child's vertical has to fall on its parent's expand/collapse chevron, so
 * the arrow points straight down the line into its children. The group cell
 * lays out as `12px padding + depth × INDENT_STEP spacer + 24px chevron box`,
 * which puts chevron centres at 24px, 52px, 80px … from the cell's left edge.
 */
export const GUIDE_ORIGIN = 24
export const GUIDE_STEP = 28
export const GUIDE_WIDTH = 2
/** Elbows stop short of what they point at: the chevron, or the label itself. */
const ELBOW_TO_CHEVRON = 14
const ELBOW_TO_LABEL = 35

const centreOf = (column: number) => GUIDE_ORIGIN + GUIDE_STEP * column

/**
 * Draws the hierarchy into the group cell's empty indent gutter.
 *
 * A vertical line per ancestor that still has rows below it, and an elbow into
 * this row's own label — so the last child of a branch ends in an L and the
 * ones above it in a T, which is what says "the branch stops here". Painted as
 * background layers because the shape depends on the row's position among its
 * siblings; a CSS class per level cannot express that.
 */
export const treeGuideStyle = (
  meta: GridTreeMeta | undefined,
  color: string,
): CSSProperties | null => {
  if (!meta || meta.depth < 1) return null

  const lines: { x: number; y: string; w: number; h: string }[] = []

  // Column `c` carries the line belonging to the ancestor at depth `c + 1`:
  // an ancestor's children hang off its chevron, one column to its left. The
  // row's own connector owns column `depth - 1`, so the ancestor lines stop
  // one short of it.
  for (let column = 0; column <= meta.depth - 2; column++) {
    if (!meta.ancestorContinues[column + 1]) continue
    lines.push({
      x: centreOf(column) - GUIDE_WIDTH / 2,
      y: '0',
      w: GUIDE_WIDTH,
      h: '100%',
    })
  }

  const ownCentre = centreOf(meta.depth - 1)

  lines.push({
    x: ownCentre - GUIDE_WIDTH / 2,
    y: '0',
    w: GUIDE_WIDTH,
    h: meta.isLastSibling ? '50%' : '100%',
  })
  lines.push({
    x: ownCentre - GUIDE_WIDTH / 2,
    y: `calc(50% - ${GUIDE_WIDTH / 2}px)`,
    w: meta.hasChildren ? ELBOW_TO_CHEVRON : ELBOW_TO_LABEL,
    h: `${GUIDE_WIDTH}px`,
  })

  return {
    backgroundImage: lines.map(() => `linear-gradient(${color}, ${color})`).join(', '),
    backgroundSize: lines.map((line) => `${line.w}px ${line.h}`).join(', '),
    backgroundPosition: lines.map((line) => `${line.x}px ${line.y}`).join(', '),
    backgroundRepeat: 'no-repeat',
  }
}
